import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Chip, Alert, Skeleton, Link,
} from '@mui/material';
import {
  TrendingUp, Inventory, Receipt, People, Category,
  MonetizationOn, AssignmentReturn, LocalShipping, Widgets,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { shopifyAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const fmtMoney = (n: number | null | undefined) =>
  `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const ShopifyDashboard: React.FC = () => {
  const [active, setActive] = useUrlIntState('shop_tab', 0);

  const { data: overview } = useQuery({ queryKey: ['shopify-overview'], queryFn: shopifyAPI.getOverview, staleTime: 60000 });
  const { data: revenue } = useQuery({ queryKey: ['shopify-revenue'], queryFn: () => shopifyAPI.getRevenue(365), staleTime: 60000 });
  const { data: products } = useQuery({ queryKey: ['shopify-products'], queryFn: shopifyAPI.getProducts, staleTime: 60000 });
  const { data: orders } = useQuery({ queryKey: ['shopify-orders'], queryFn: () => shopifyAPI.getOrders(), staleTime: 60000 });
  const { data: customers } = useQuery({ queryKey: ['shopify-customers'], queryFn: () => shopifyAPI.getCustomers(), staleTime: 60000 });
  const { data: productRevenue } = useQuery({ queryKey: ['shopify-product-revenue'], queryFn: shopifyAPI.getProductRevenue, staleTime: 60000 });
  const { data: variants } = useQuery({ queryKey: ['shopify-variants'], queryFn: shopifyAPI.getVariants, staleTime: 60000 });
  const { data: collections } = useQuery({ queryKey: ['shopify-collections'], queryFn: shopifyAPI.getCollections, staleTime: 60000 });
  const { data: transactions } = useQuery({ queryKey: ['shopify-transactions'], queryFn: () => shopifyAPI.getTransactions(200), staleTime: 60000 });
  const { data: refunds } = useQuery({ queryKey: ['shopify-refunds'], queryFn: shopifyAPI.getRefunds, staleTime: 60000 });
  const { data: fulfillments } = useQuery({ queryKey: ['shopify-fulfillments'], queryFn: shopifyAPI.getFulfillments, staleTime: 60000 });

  if (overview && !overview.configured) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <SectionTitle sub="&gt; Orders, revenue, product performance_">
            SHOPIFY :: MERCH CONSOLE
          </SectionTitle>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure SHOPIFY_ACCESS_TOKEN to start syncing Shopify data.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Orders, revenue, product performance_">
          SHOPIFY :: MERCH CONSOLE
        </SectionTitle>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'TOTAL ORDERS', value: fmt(overview?.order_count) },
          { label: 'REVENUE', value: fmtMoney(overview?.total_revenue) },
          { label: 'AVG ORDER VALUE', value: fmtMoney(overview?.avg_order_value) },
          { label: 'PRODUCTS', value: fmt(overview?.product_count) },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 3 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<TrendingUp />} label="Revenue" />
          <Tab icon={<Inventory />} label="Products" />
          <Tab icon={<Widgets />} label="Variants" />
          <Tab icon={<Category />} label="Collections" />
          <Tab icon={<Receipt />} label="Orders" />
          <Tab icon={<MonetizationOn />} label="Transactions" />
          <Tab icon={<AssignmentReturn />} label="Refunds" />
          <Tab icon={<LocalShipping />} label="Fulfillments" />
          <Tab icon={<People />} label="Customers" />
        </Tabs>
      </Paper>

      {/* Revenue tab */}
      <TabPanel value={active} index={0}>
        <Panel label="DAILY REVENUE">
          {revenue?.available ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={revenue.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v: number) => fmtMoney(v)} />
                <Area type="monotone" dataKey="total_revenue" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Skeleton variant="rectangular" height={300} />}
        </Panel>
      </TabPanel>

      {/* Products tab */}
      <TabPanel value={active} index={1}>
        <Panel label="PRODUCT CATALOG">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell>VENDOR</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell align="right">SOLD</TableCell>
                  <TableCell align="right">REVENUE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(products?.products || []).map((p: any) => (
                  <TableRow key={p.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>{p.title}</Typography></TableCell>
                    <TableCell>{p.product_type || '—'}</TableCell>
                    <TableCell>{p.vendor || '—'}</TableCell>
                    <TableCell>
                      <Chip label={p.status || 'active'} size="small" color={p.status === 'active' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">{fmt(p.total_sold)}</TableCell>
                    <TableCell align="right">{fmtMoney(p.total_revenue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Orders tab */}
      <TabPanel value={active} index={4}>
        <Panel label="RECENT ORDERS">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ORDER #</TableCell>
                  <TableCell>DATE</TableCell>
                  <TableCell>EMAIL</TableCell>
                  <TableCell align="right">TOTAL</TableCell>
                  <TableCell align="right">REFUNDS</TableCell>
                  <TableCell>PAYMENT</TableCell>
                  <TableCell>FULFILLMENT</TableCell>
                  <TableCell align="right">ITEMS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(orders?.orders || []).map((o: any) => (
                  <TableRow key={o.id} hover>
                    <TableCell><Typography variant="body2" fontWeight={600}>#{o.order_number}</Typography></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{o.created_at ? new Date(o.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{o.email || '—'}</TableCell>
                    <TableCell align="right">{fmtMoney(o.total_price)}</TableCell>
                    <TableCell align="right" sx={{ color: o.refund_total > 0 ? 'error.main' : 'inherit' }}>
                      {o.refund_total > 0 ? fmtMoney(o.refund_total) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={o.financial_status || '—'} size="small" color={o.financial_status === 'paid' ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip label={o.fulfillment_status || 'unfulfilled'} size="small" color={o.fulfillment_status === 'fulfilled' ? 'success' : 'warning'} />
                    </TableCell>
                    <TableCell align="right">{o.line_item_count ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Showing {orders?.orders?.length || 0} of {orders?.total || 0} orders
          </Typography>
        </Panel>
      </TabPanel>

      {/* Customers tab */}
      <TabPanel value={active} index={8}>
        <Panel label="CUSTOMERS">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>NAME</TableCell>
                  <TableCell>EMAIL</TableCell>
                  <TableCell align="right">ORDERS</TableCell>
                  <TableCell align="right">TOTAL SPENT</TableCell>
                  <TableCell align="right">LTV INDICATOR</TableCell>
                  <TableCell>JOINED</TableCell>
                  <TableCell>TAGS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(customers?.customers || []).map((c: any) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Guest'}
                      </Typography>
                    </TableCell>
                    <TableCell>{c.email || '—'}</TableCell>
                    <TableCell align="right">{fmt(c.orders_count)}</TableCell>
                    <TableCell align="right">{fmtMoney(c.total_spent)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={c.total_spent >= 200 ? 'HIGH' : c.total_spent >= 50 ? 'MED' : 'LOW'}
                        size="small"
                        color={c.total_spent >= 200 ? 'success' : c.total_spent >= 50 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                        {c.tags || '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Showing {customers?.customers?.length || 0} of {customers?.total || 0} customers
          </Typography>
        </Panel>

        {productRevenue?.products?.length > 0 && (
          <Panel label="PRODUCT-LEVEL REVENUE" sx={{ mt: 3 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>PRODUCT</TableCell>
                    <TableCell align="right">UNITS SOLD</TableCell>
                    <TableCell align="right">REVENUE</TableCell>
                    <TableCell align="right">ORDERS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productRevenue.products.map((p: any, i: number) => (
                    <TableRow key={p.product_id || i} hover>
                      <TableCell><Typography variant="body2" fontWeight={600}>{p.title || 'Unknown'}</Typography></TableCell>
                      <TableCell align="right">{fmt(p.total_sold)}</TableCell>
                      <TableCell align="right">{fmtMoney(p.total_revenue)}</TableCell>
                      <TableCell align="right">{fmt(p.order_count)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Panel>
        )}
      </TabPanel>

      {/* Variants */}
      <TabPanel value={active} index={2}>
        <Panel label={`VARIANTS (${fmt(variants?.variants?.length)} SKUs)`}>
          <Box sx={{ overflowX: 'auto', maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>PRODUCT</TableCell>
                  <TableCell>VARIANT</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">PRICE</TableCell>
                  <TableCell align="right">COMPARE AT</TableCell>
                  <TableCell align="right">INVENTORY</TableCell>
                  <TableCell>OPTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(variants?.variants || []).map((v: any) => (
                  <TableRow key={v.id} hover>
                    <TableCell>{v.product_title || '—'}</TableCell>
                    <TableCell>{v.variant_title || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px' }}>{v.sku || '—'}</TableCell>
                    <TableCell align="right">{v.price != null ? fmtMoney(v.price) : '—'}</TableCell>
                    <TableCell align="right" sx={{ color: v.compare_at_price ? '#ff6600' : undefined }}>
                      {v.compare_at_price ? fmtMoney(v.compare_at_price) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Chip size="small" label={fmt(v.inventory_quantity)}
                        sx={{
                          background: 'transparent',
                          color: (v.inventory_quantity ?? 0) > 0 ? '#00ff41' : '#ff3344',
                          border: `1px solid ${(v.inventory_quantity ?? 0) > 0 ? '#00ff41' : '#ff3344'}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {[v.option1, v.option2, v.option3].filter(Boolean).join(' / ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Collections */}
      <TabPanel value={active} index={3}>
        <Panel label={`COLLECTIONS (${fmt(collections?.collections?.length)})`}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>HANDLE</TableCell>
                  <TableCell>TYPE</TableCell>
                  <TableCell align="right">PRODUCTS</TableCell>
                  <TableCell>PUBLISHED</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(collections?.collections || []).map((c: any) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        {c.image_src && <img src={c.image_src} alt="" style={{ width: 28, height: 28, objectFit: 'cover' }} />}
                        <Typography variant="body2" fontWeight={600}>{c.title}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px', color: '#5aaa5a' }}>{c.handle}</TableCell>
                    <TableCell>
                      <Chip size="small" label={c.collection_type}
                        sx={{ background: 'transparent', border: `1px solid ${c.collection_type === 'smart' ? '#00ffff' : '#00ff41'}`, color: c.collection_type === 'smart' ? '#00ffff' : '#00ff41' }}
                      />
                    </TableCell>
                    <TableCell align="right">{fmt(c.products_count ?? c.linked_products)}</TableCell>
                    <TableCell><Chip size="small" label={c.published ? 'YES' : 'NO'} color={c.published ? 'success' : 'default'} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Transactions */}
      <TabPanel value={active} index={5}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="REVENUE BY GATEWAY">
              {transactions?.rollup?.length ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={transactions.rollup.filter((r: any) => r.kind === 'sale')}
                      dataKey="total"
                      nameKey="gateway"
                      cx="50%" cy="50%"
                      outerRadius={110}
                      label={(e: any) => `${e.gateway}: ${fmtMoney(e.total)}`}
                    >
                      {transactions.rollup.map((_: any, i: number) => (
                        <Cell key={i} fill={["#00ff41", "#00ffff", "#ffb000", "#ff6600", "#ff3344"][i % 5]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v: any) => fmtMoney(Number(v))} contentStyle={{ background: '#0a0a0a', border: '1px solid #1e3a1e', color: '#00ff41' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={300} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="ROLLUP">
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>KIND</TableCell><TableCell>GATEWAY</TableCell><TableCell align="right">COUNT</TableCell><TableCell align="right">TOTAL</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {(transactions?.rollup || []).map((r: any, i: number) => (
                    <TableRow key={i} hover>
                      <TableCell>{r.kind}</TableCell>
                      <TableCell>{r.gateway || '—'}</TableCell>
                      <TableCell align="right">{fmt(r.count)}</TableCell>
                      <TableCell align="right">{fmtMoney(r.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Panel>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Panel label="RECENT TRANSACTIONS">
              <Box sx={{ overflowX: 'auto', maxHeight: 420 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>PROCESSED</TableCell>
                      <TableCell>KIND</TableCell>
                      <TableCell>STATUS</TableCell>
                      <TableCell>GATEWAY</TableCell>
                      <TableCell align="right">AMOUNT</TableCell>
                      <TableCell align="right">FEE</TableCell>
                      <TableCell>ORDER</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(transactions?.recent || []).map((t: any) => (
                      <TableRow key={t.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.processed_at ? new Date(t.processed_at).toLocaleString() : '—'}</TableCell>
                        <TableCell>{t.kind}</TableCell>
                        <TableCell><Chip size="small" label={t.status} color={t.status === 'success' ? 'success' : 'default'} /></TableCell>
                        <TableCell>{t.gateway}</TableCell>
                        <TableCell align="right">{t.amount != null ? fmtMoney(t.amount) : '—'}</TableCell>
                        <TableCell align="right">{t.fee ? fmtMoney(t.fee) : '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>#{t.order_id}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Refunds */}
      <TabPanel value={active} index={6}>
        <Panel label={`REFUNDS (${fmt(refunds?.refunds?.length)})`}>
          {(refunds?.refunds || []).length === 0 ? (
            <Alert severity="success">No refunds on record. 🎉</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>DATE</TableCell>
                  <TableCell>ORDER</TableCell>
                  <TableCell align="right">AMOUNT</TableCell>
                  <TableCell align="right">ITEMS</TableCell>
                  <TableCell>NOTE</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(refunds?.refunds || []).map((r: any) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>#{r.order_id}</TableCell>
                    <TableCell align="right" sx={{ color: '#ff6600' }}>{fmtMoney(r.total_refunded)}</TableCell>
                    <TableCell align="right">{fmt(r.items_count)}</TableCell>
                    <TableCell>{r.note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      </TabPanel>

      {/* Fulfillments */}
      <TabPanel value={active} index={7}>
        <Panel label={`FULFILLMENTS (${fmt(fulfillments?.fulfillments?.length)})`}>
          {(fulfillments?.fulfillments || []).length === 0 ? (
            <Alert severity="info">No fulfillment events yet — orders may still be pending ship.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CREATED</TableCell>
                  <TableCell>ORDER</TableCell>
                  <TableCell>STATUS</TableCell>
                  <TableCell>SHIPMENT</TableCell>
                  <TableCell>CARRIER</TableCell>
                  <TableCell>TRACKING</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(fulfillments?.fulfillments || []).map((f: any) => (
                  <TableRow key={f.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.created_at ? new Date(f.created_at).toLocaleString() : '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>#{f.order_id}</TableCell>
                    <TableCell>{f.status || '—'}</TableCell>
                    <TableCell>{f.shipment_status || '—'}</TableCell>
                    <TableCell>{f.tracking_company || '—'}</TableCell>
                    <TableCell>
                      {f.tracking_url ? (
                        <Link href={f.tracking_url} target="_blank" rel="noopener" sx={{ color: '#00ff41', fontFamily: 'monospace', fontSize: '12px' }}>
                          {f.tracking_number || '(track)'}
                        </Link>
                      ) : (f.tracking_number || '—')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>
      </TabPanel>
    </Container>
  );
};

export default ShopifyDashboard;
